import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Chip,
  Box,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaEdit, FaTrash, FaCloudUploadAlt, FaCloudDownloadAlt, FaEye, FaEyeSlash, FaSearch } from 'react-icons/fa';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, ResourcesGrid, ResourceCard, ResourceCardContent, ResourceDetailsGrid, ItalicText, ResourceButtons } from './styles';
import EmptyState from '../../common/EmptyState';
import Modal from '../../common/Modal';
import ConfirmationModal from '../../common/ConfirmationModal';
import LoadingSpinner from '../../common/LoadingSpinner';
import { COLORS } from '../../../constants/radar';
import { useAlert } from '../../../contexts/AlertContext';
import { permissionsApi } from '../../../services/api/permissionsApi';
import { toLocalDate } from '../../../utils/dateOnly';

const Resources = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [searchTerm, setSearchTerm] = useState('');
  const [resourcesData, setResourcesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'create', // 'create' only - edit is now done on detail page
    resource: null,
    formData: {
      name: '',
      description: ''
    },
    submitting: false
  });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    resource: null,
    deleting: false
  });

  // Load resources data on component mount
  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      setLoading(true);
      const resources = await permissionsApi.getAllResources();
      setResourcesData(resources);
    } catch (error) {
      console.error('Error loading resources:', error);
      showAlert(error.message || 'Failed to load resources', 'error');
      setResourcesData([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredResources = resourcesData.filter(resource => 
    resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (resource.description && resource.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handlePublishToggle = async (resource) => {
    try {
      const updatedResource = resource.isPublished 
        ? await permissionsApi.unpublishResource(resource.id)
        : await permissionsApi.publishResource(resource.id);
      
      setResourcesData(prevResources => 
        prevResources.map(r => 
          r.id === resource.id ? { ...r, ...updatedResource, permissionsCount: r.permissionsCount } : r
        )
      );
      
      showAlert(
        `Resource ${resource.isPublished ? 'unpublished' : 'published'} successfully`, 
        'success'
      );
    } catch (error) {
      console.error('Error toggling resource publish status:', error);
      showAlert(error.message || `Failed to ${resource.isPublished ? 'unpublish' : 'publish'} resource`, 'error');
    }
  };


  const getStatusColor = (isPublished) => {
    return isPublished ? '#E6F4EA' : '#F3F4F6';
  };

  const getStatusTextColor = (isPublished) => {
    return isPublished ? '#1E8E3E' : '#6B7280';
  };

  const getStatusText = (isPublished) => {
    return isPublished ? 'Published' : 'Draft';
  };

  // Modal handlers
  const openCreateModal = () => {
    setModalState({
      isOpen: true,
      mode: 'create',
      resource: null,
      formData: {
        name: '',
        description: ''
      }
    });
  };

  const handleResourceClick = (resource) => {
    navigate(`/permissions/resources/${resource.id}`);
  };

  const handleModalClose = () => {
    setModalState({
      isOpen: false,
      mode: 'create',
      resource: null,
      formData: {
        name: '',
        description: ''
      },
      submitting: false
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setModalState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [name]: value
      }
    }));
  };

  const isFormValid = () => {
    const { name } = modalState.formData;
    return name.trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    const { name, description } = modalState.formData;
    
    try {
      setModalState(prev => ({ ...prev, submitting: true }));
      
      // Create new resource
      const newResource = await permissionsApi.createResource({
        name: name,
        description: description || undefined
      });
      
      setResourcesData(prev => [...prev, newResource]);
      showAlert('Resource created successfully', 'success');
      handleModalClose();
    } catch (error) {
      console.error('Error creating resource:', error);
      showAlert(error.message || 'Failed to create resource', 'error');
    } finally {
      setModalState(prev => ({ ...prev, submitting: false }));
    }
  };

  // Delete handlers
  const handleDeleteClick = (resource) => {
    setDeleteModal({
      open: true,
      resource
    });
  };

  const handleDelete = async () => {
    if (!deleteModal.resource) return;
    
    try {
      setDeleteModal(prev => ({ ...prev, deleting: true }));
      
      await permissionsApi.deleteResource(deleteModal.resource.id);
      
      // Remove resource from the list
      setResourcesData(prev => prev.filter(resource => resource.id !== deleteModal.resource.id));
      
      showAlert('Resource deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting resource:', error);
      showAlert(error.message || 'Failed to delete resource', 'error');
    } finally {
      // Close the modal
      setDeleteModal({ open: false, resource: null, deleting: false });
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/permissions')}>
            <FaArrowLeft /> Back to Permissions
          </BackButton>
          <Title>Resources</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton onClick={() => setShowControls(!showControls)}>
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <TextField
              placeholder="Search Resources"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                flex: 1,
                maxWidth: '500px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  height: '40px',
                  fontSize: '14px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  }
                },
                '& .MuiInputBase-input': {
                  padding: '8px 14px',
                }
              }}
            />

            <HeaderButton 
              onClick={openCreateModal}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Create Resource
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer show={showControls}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2
          }}>
            <Typography 
              sx={{ 
                color: '#64748B',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>
                {loading ? '...' : filteredResources.length}
              </Box>
              {filteredResources.length === 1 ? 'resource' : 'resources'} found
            </Typography>
          </Box>

          {loading ? (
            <LoadingSpinner message="Loading resources..." />
          ) : filteredResources.length === 0 ? (
            <EmptyState
              title="No resources found"
              description={searchTerm ? 
                "No resources match your search criteria. Try adjusting your search term." : 
                "No resources have been created yet. Click the 'Create Resource' button to create your first resource."}
            />
          ) : (
            <ResourcesGrid>
              {filteredResources.map((resource) => (
                <ResourceCard 
                  key={resource.id || resource.name}
                >
                  <ResourceCardContent>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 2,
                      gap: 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          sx={{ 
                            color: COLORS.PRIMARY,
                            fontSize: '18px',
                            fontWeight: 600,
                            marginBottom: '8px'
                          }}
                        >
                          {resource.name}
                        </Typography>
                      </Box>
                      <Chip
                        label={getStatusText(resource.isPublished)}
                        size="small"
                        sx={{ 
                          backgroundColor: getStatusColor(resource.isPublished),
                          color: getStatusTextColor(resource.isPublished),
                          border: '1px solid',
                          borderColor: `${getStatusTextColor(resource.isPublished)}20`,
                          height: '24px',
                          minWidth: 'fit-content',
                          '& .MuiChip-label': {
                            fontSize: '13px',
                            padding: '0 8px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                    </Box>
                    
                    <ResourceDetailsGrid>
                      {resource.description ? (
                        <Typography color="#333">
                          {resource.description}
                        </Typography>
                      ) : (
                        <ItalicText>No description provided</ItalicText>
                      )}
                      {resource.publishedAt && resource.isPublished && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Published: {toLocalDate(resource.publishedAt)?.toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Permissions Count */}
                      <Box sx={{
                        marginTop: '16px',
                        paddingTop: '12px',
                        borderTop: '1px solid #E2E8F0',
                        fontSize: '13px',
                        color: '#475569'
                      }}>
                        {resource.permissionsCount > 0 ? (
                          <Box component="span" sx={{ fontWeight: '500' }}>
                            <Box component="span" sx={{ fontWeight: '700', fontSize: '15px' }}>
                              {resource.permissionsCount}
                            </Box> permission{resource.permissionsCount === 1 ? '' : 's'} defined
                          </Box>
                        ) : (
                          <Box component="span" sx={{ fontStyle: 'italic', color: '#94A3B8' }}>
                            No permissions defined
                          </Box>
                        )}
                      </Box>
                    </ResourceDetailsGrid>

                    <ResourceButtons>
                      {resource.isPublished ? (
                        <IconButton
                          onClick={() => handlePublishToggle(resource)}
                          color="primary"
                          size="small"
                          title="Unpublish resource"
                        >
                          <FaCloudDownloadAlt />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={() => handlePublishToggle(resource)}
                          color="primary"
                          size="small"
                          title="Publish resource"
                        >
                          <FaCloudUploadAlt />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() => handleResourceClick(resource)}
                        color="primary"
                        size="small"
                        title="Edit resource"
                      >
                        <FaEdit />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(resource)}
                        color="error"
                        size="small"
                        title="Delete resource"
                      >
                        <FaTrash />
                      </IconButton>
                    </ResourceButtons>
                  </ResourceCardContent>
                </ResourceCard>
              ))}
            </ResourcesGrid>
          )}
        </MainContainer>
      </Content>

      {/* Create Resource Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={handleModalClose}
        title='Create Resource'
        onSubmit={handleSubmit}
        submitText='Create'
        submitDisabled={!isFormValid() || modalState.submitting}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            name="name"
            required
            fullWidth
            variant="outlined"
            placeholder="Enter resource name (e.g. FeedbackSession, Employee)"
            value={modalState.formData.name}
            onChange={handleInputChange}
            autoComplete="off"
          />
          <TextField
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="Enter resource description"
            value={modalState.formData.description}
            onChange={handleInputChange}
            autoComplete="off"
          />
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, resource: null, deleting: false })}
        onConfirm={handleDelete}
        title="Delete Resource"
        message={`Are you sure you want to delete the "${deleteModal.resource?.name}" resource? This action cannot be undone.`}
        loading={deleteModal.deleting}
      />
    </Container>
  );
};

export default Resources;